import { Modal } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { Store } from '../../plans/services/PlanCRUD';
import { createStoreListing } from '../services/StoreListingCRUD';
import { listProductVariants } from '../../product-listing/services/ProductVariantCRUD';
import { listTaxClasses, TaxClassModel } from '../services/TaxClassCRUD';
import ProductVariantModel from '../../product-listing/models/ProductVartiant';

interface StoreListingModalFormProps {
  stores: Store[];
  onClose: () => void;
  onCreated: () => void;
  visible: boolean;
}

export default function StoreListingModalForm({
  stores,
  onClose,
  onCreated,
  visible,
}: StoreListingModalFormProps) {
  const intl = useIntl();
  const [products, setProducts] = useState<ProductVariantModel[]>([]);
  const [taxClasses, setTaxClasses] = useState<TaxClassModel[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ProductVariantModel | null>(null);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [selectedTaxClass, setSelectedTaxClass] = useState<TaxClassModel | null>(null);
  const [price, setPrice] = useState<number | null>(null);
  const [state, setState] = useState<'active' | 'inactive'>('active');

  const productOptions = products.map((p) => ({
    value: p.id,
    label: `${p.name} - ${p.ean}`,
  }));

  const storeOptions = stores.map((s) => ({
    value: s.id,
    label: s.name,
  }));

  const taxClassOptions = taxClasses.map((tc) => ({
    value: tc.id,
    label: `${tc.name} (${tc.country})`,
  }));

  async function fetchProducts() {
    try {
      const { data } = await listProductVariants();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }

  async function fetchTaxClasses() {
    try {
      const { data } = await listTaxClasses();
      setTaxClasses(data);
    } catch (error) {
      console.error('Error fetching tax classes:', error);
    }
  }

  async function onSubmit() {
    try {
      await createStoreListing({
        product: selectedProduct!.id,
        store: selectedStore!.id,
        tax_class: selectedTaxClass!.id,
        price: price!,
        state,
      });
      onCreated();
      toast.success(intl.formatMessage({ id: 'storeListing.createSuccess' }));
    } catch (error) {
      const responseData = error?.response?.data;
      const errorMessage =
        responseData?.non_field_errors?.[0] ||
        (responseData && Object.values(responseData).flat()[0]) ||
        intl.formatMessage({ id: 'storeListing.createError' });
      toast.error(errorMessage as string);
    }
  }

  useEffect(() => {
    fetchProducts();
    fetchTaxClasses();
  }, []);

  useEffect(() => {
    if (!visible) {
      setSelectedProduct(null);
      setSelectedStore(null);
      setSelectedTaxClass(null);
      setPrice(null);
      setState('active');
    }
  }, [visible]);

  const isSubmitDisabled =
    !selectedProduct || !selectedStore || !selectedTaxClass || price === null;

  return (
    <Modal show={visible} onHide={onClose} backdrop="static" keyboard={false} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage({ id: 'storeListing.createStoreListing' })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column gap-4">
          <div className="form-group">
            <label className="form-label">
              {intl.formatMessage({ id: 'storeListing.product' })}
            </label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={productOptions}
              value={productOptions.find((o) => o.value === selectedProduct?.id) || null}
              onChange={(opt) => {
                const product = products.find((p) => p.id === opt?.value);
                setSelectedProduct(product || null);
              }}
              placeholder={intl.formatMessage({ id: 'storeListing.selectProduct' })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">
              {intl.formatMessage({ id: 'storeListing.store' })}
            </label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={storeOptions}
              value={storeOptions.find((o) => o.value === selectedStore?.id) || null}
              onChange={(opt) => {
                const store = stores.find((s) => s.id === opt?.value);
                setSelectedStore(store || null);
                setSelectedTaxClass(null);
              }}
              placeholder={intl.formatMessage({ id: 'storeListing.selectStore' })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">
              {intl.formatMessage({ id: 'storeListing.taxClass' })}
            </label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={taxClassOptions}
              value={taxClassOptions.find((o) => o.value === selectedTaxClass?.id) || null}
              onChange={(opt) => {
                const taxClass = taxClasses.find((tc) => tc.id === opt?.value);
                setSelectedTaxClass(taxClass || null);
              }}
              placeholder={intl.formatMessage({ id: 'storeListing.selectTaxClass' })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">
              {intl.formatMessage({ id: 'storeListing.price' })} (€)
            </label>
            <input
              type="number"
              className="form-control"
              step={0.01}
              min={0}
              value={price ?? ''}
              onChange={(e) =>
                setPrice(e.target.value.length > 0 ? Number(e.target.value) : null)
              }
            />
          </div>
          <div className="form-group">
            <label className="form-label">
              {intl.formatMessage({ id: 'storeListing.stateLabel' })}
            </label>
            <select
              className="form-select"
              value={state}
              onChange={(e) => setState(e.target.value as 'active' | 'inactive')}
            >
              <option value="active">
                {intl.formatMessage({ id: 'storeListing.stateActive' })}
              </option>
              <option value="inactive">
                {intl.formatMessage({ id: 'storeListing.stateInactive' })}
              </option>
            </select>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary" onClick={onClose}>
          {intl.formatMessage({ id: 'common.cancel' })}
        </button>
        <button
          className="btn btn-primary"
          onClick={onSubmit}
          disabled={isSubmitDisabled}
        >
          {intl.formatMessage({ id: 'common.create' })}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
